import React from 'react';
import { Quest } from '../types';

interface QuestDetailsModalProps {
  quest: Quest;
  isOpen: boolean;
  onClose: () => void;
}

const QuestDetailsModal: React.FC<QuestDetailsModalProps> = ({ quest, isOpen, onClose }) => {
  if (!isOpen) return null;

  const hasResources = quest.resources && quest.resources.length > 0;
  const hasSubTasks = quest.subTasks && quest.subTasks.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 animate-fade-in modal-backdrop"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 pointer-events-none modal-container">
        <div 
          className="bg-gradient-to-br from-system-darker to-system-dark border-2 border-system-blue rounded-lg shadow-neon-blue max-w-3xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-system-blue/20 to-system-purple/20 border-b-2 border-system-blue/30 p-3 sm:p-6 flex items-start justify-between gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <div className="text-2xl sm:text-4xl">📋</div>
                <h2 className="font-orbitron text-base sm:text-2xl font-bold text-system-blue uppercase tracking-wider">
                  Quest Details
                </h2>
              </div>
              <h3 className="text-base sm:text-xl font-bold text-white font-rajdhani mb-1 sm:mb-2">
                {quest.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-300 font-rajdhani">
                {quest.description}
              </p>
            </div>
            <button
              onClick={onClose}
              className="system-btn system-btn-danger px-2 sm:px-3 py-1 sm:py-2 text-lg sm:text-xl font-bold shrink-0"
              title="Close"
            >
              <span className="relative z-10">×</span>
            </button>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 space-y-3 sm:space-y-6">
            {/* Sub-Tasks */}
            {hasSubTasks && (
              <div className="bg-system-darker border-2 border-system-blue/30 rounded-lg p-3 sm:p-5">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-4">
                  <div className="w-0.5 sm:w-1 h-5 sm:h-6 bg-system-blue shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                  <h4 className="font-orbitron text-sm sm:text-lg font-bold text-system-blue uppercase tracking-wider">
                    ✅ Step-by-Step Guide
                  </h4>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {quest.subTasks?.map((subTask, index) => (
                    <li key={subTask.id} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-system-dark/50 rounded-lg border border-system-blue/20 hover:border-system-blue/50 transition-all">
                      <div className="flex-shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-gradient-to-br from-system-blue to-system-purple flex items-center justify-center font-orbitron font-bold text-xs sm:text-sm text-white shadow-lg">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <span className={`font-rajdhani text-sm sm:text-base ${subTask.completed ? 'text-green-400 line-through' : 'text-gray-200'}`}>
                          {subTask.text}
                        </span>
                        {subTask.completed && (
                          <span className="ml-1 sm:ml-2 text-green-400">✓</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Resources */}
            {hasResources && (
              <div className="bg-system-darker border-2 border-green-500/30 rounded-lg p-3 sm:p-5">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3">
                  <div className="w-0.5 sm:w-1 h-5 sm:h-6 bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]"></div>
                  <h4 className="font-orbitron text-sm sm:text-lg font-bold text-green-400 uppercase tracking-wider">
                    🔗 Resources & Tools
                  </h4>
                </div>
                <ul className="space-y-1.5 sm:space-y-2">
                  {quest.resources?.map((resource, index) => {
                    // Check if it's a URL
                    const isUrl = resource.startsWith('http://') || resource.startsWith('https://');
                    
                    return (
                      <li key={index} className="flex items-start gap-2 sm:gap-3">
                        <span className="text-green-400 text-base sm:text-lg mt-0.5">🔗</span>
                        {isUrl ? (
                          <a
                            href={resource}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-green-400 hover:text-green-300 underline font-rajdhani text-sm sm:text-base transition-colors break-all"
                          >
                            {resource}
                          </a>
                        ) : (
                          <span className="flex-1 text-gray-200 font-rajdhani text-sm sm:text-base">{resource}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Empty State */}
            {!hasSubTasks && !hasResources && (
              <div className="text-center py-8 sm:py-12">
                <div className="text-4xl sm:text-6xl mb-2 sm:mb-4">📋</div>
                <div className="text-gray-400 font-rajdhani text-base sm:text-lg">
                  No additional details available for this quest.
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gradient-to-r from-system-darker to-system-dark border-t-2 border-system-blue/30 p-3 sm:p-4">
            <button
              onClick={onClose}
              className="w-full system-btn system-btn-primary py-2 sm:py-3 font-orbitron font-bold text-xs sm:text-sm tracking-widest uppercase"
            >
              <span className="relative z-10">［ CLOSE ］</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestDetailsModal;

